import { LanguageSelect } from '@/components';
import { Col, Container, Row, Dropdown, DropdownToggle, DropdownMenu, DropdownDivider } from 'react-bootstrap';
import { BsCalendar, BsDashCircle, BsPeople, BsPlusCircle, BsGeoAlt } from 'react-icons/bs';
import Flatpicker from '@/components/Flatpicker';
import { excursionsService, accommodationsService } from '@/api/services';
import { useRegionsList } from '@/hooks';
import { useSearchParams } from '@/compat/react-router-dom';
import React, { useEffect, useState, useCallback, useRef, startTransition } from 'react';
import { getBookSectionSurfaceClasses } from '@/utils/bookSectionSurface';
import { formatYMD } from '@/utils/excursionAvailability';

const DEFAULT_RANGE_DAYS = 5;

const shallowEqual = (a, b) => {
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((val, idx) => {
      if (val instanceof Date && b[idx] instanceof Date) {
        return val.getTime() === b[idx].getTime();
      }
      return val === b[idx];
    });
  }

  if (a && b && typeof a === 'object' && typeof b === 'object') {
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    return keysA.every((key) => a[key] === b[key]);
  }

  return a === b;
};

const startOfToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const getDefaultDateRange = () => {
  const dateFrom = startOfToday();
  return { dateFrom, dateTo: addDays(dateFrom, DEFAULT_RANGE_DAYS) };
};

/**
 * Excursion hero search (dates, guests, language, pickup).
 * @param {'hero'|'aboveResults'} variant — layout: under hero vs above tour grid
 * @param {{ backgroundColor?: string, backgroundEffect?: string|null }|null} bandSurface — when `variant` is `aboveResults`, applies Site Builder background (inactive section + search strip).
 */
const ExcursionsSearchPanel = ({
  variant = 'hero',
  searchOnly = false,
  showSearchForm = true,
  bandSurface = null,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const parseDate = useCallback((value) => {
    if (!value) return null;
    const parsed = new Date(`${value}T00:00:00`);
    return isNaN(parsed.getTime()) ? null : parsed;
  }, []);

  const getInitialFormData = useCallback(() => {
    const dateFromParam = searchParams.get('dateFrom');
    const dateToParam = searchParams.get('dateTo');
    const singleDateParam = searchParams.get('date');

    let dateFrom = parseDate(dateFromParam);
    let dateTo = parseDate(dateToParam);
    const singleDate = parseDate(singleDateParam);

    if (!dateFrom && !dateTo && singleDate) {
      dateFrom = singleDate;
      dateTo = singleDate;
    }
    if (!dateFrom && !dateTo) {
      const defaults = getDefaultDateRange();
      dateFrom = defaults.dateFrom;
      dateTo = defaults.dateTo;
    }
    if (dateFrom && !dateTo) dateTo = dateFrom;
    if (dateTo && !dateFrom) dateFrom = dateTo;

    return {
      dateFrom,
      dateTo,
      language: searchParams.get('language') || null,
      pickupArea: searchParams.get('pickupArea') || null,
      passengers: {
        adults: Number(searchParams.get('adults')) || 2,
        children: Number(searchParams.get('children')) || 0,
        infants: Number(searchParams.get('infants')) || 0,
      },
    };
  }, [parseDate, searchParams]);

  const [formData, setFormData] = useState(() => getInitialFormData());
  const [regions, setRegions] = useState([]);
  const [loadingRegions, setLoadingRegions] = useState(true);
  const { data: regionsData, loading: regionsLoading, error: regionsError } = useRegionsList();

  useEffect(() => {
    setFormData(getInitialFormData());
  }, [getInitialFormData]);

  useEffect(() => {
    const fetchRegionsWithPickups = async () => {
      if (regionsLoading) {
        setLoadingRegions(true);
        return;
      }

      if (regionsError) {
        setRegions([]);
        setLoadingRegions(false);
        return;
      }

      setLoadingRegions(true);
      try {
        const [pickupsResponse, hotelsResponse] = await Promise.all([
          excursionsService.getAllPickups().catch(() => ({ data: { data: [] } })),
          accommodationsService.getAll().catch(() => ({ data: { data: [] } })),
        ]);

        const allRegions = Array.isArray(regionsData) ? regionsData : [];
        const pickups = pickupsResponse.data?.data || pickupsResponse.data || [];
        const hotels = hotelsResponse.data?.data || hotelsResponse.data || [];

        const hotelIdsWithPickups = new Set(
          pickups
            .map((pickup) => String(pickup.acc_id || pickup.accommodation_id || pickup.hotel_id || ''))
            .filter((id) => id && id !== ''),
        );

        const regionIdsWithPickups = new Set(
          hotels
            .filter((hotel) => {
              const hotelId = String(hotel.id);
              return hotelIdsWithPickups.has(hotelId);
            })
            .map((hotel) =>
              String(
                hotel.region_id ||
                  hotel.destination_region_id ||
                  hotel.region?.id ||
                  hotel.destination?.region_id ||
                  '',
              ),
            )
            .filter((id) => id && id !== ''),
        );

        const filteredRegions = allRegions.filter((region) => {
          const regionId = String(region.id);
          return regionIdsWithPickups.has(regionId);
        });

        setRegions(filteredRegions);
      } catch {
        setRegions([]);
      } finally {
        setLoadingRegions(false);
      }
    };

    fetchRegionsWithPickups();
  }, [regionsData, regionsLoading, regionsError]);

  const updateField = useCallback((field, value) => {
    setFormData((prevFormData) => {
      if (shallowEqual(prevFormData[field], value)) {
        return prevFormData;
      }
      return { ...prevFormData, [field]: value };
    });
  }, []);

  const buildSearchParams = useCallback((data) => {
    const params = new URLSearchParams();
    const fromYmd = data.dateFrom instanceof Date ? formatYMD(data.dateFrom) : '';
    const toYmd = data.dateTo instanceof Date ? formatYMD(data.dateTo) : fromYmd;

    if (fromYmd) {
      params.set('date', fromYmd);
      params.set('dateFrom', fromYmd);
      if (toYmd) params.set('dateTo', toYmd);
    }

    if (data.language && data.language !== '-1') {
      params.set('language', data.language);
    }

    if (data.pickupArea) {
      params.set('pickupArea', data.pickupArea);
    }

    if (data.passengers) {
      params.set('adults', data.passengers.adults ?? 0);
      params.set('children', data.passengers.children ?? 0);
      params.set('infants', data.passengers.infants ?? 0);
    }

    return params;
  }, []);

  const getPickupDisplayValue = useCallback(() => {
    if (formData.pickupArea === null || formData.pickupArea === undefined) return 'Pickup';
    const selectedId = String(formData.pickupArea);
    const region = regions.find((r) => String(r.id) === selectedId);
    return region ? region.name : 'Pickup';
  }, [formData.pickupArea, regions]);

  const isPickupSelected = useCallback(
    (id) =>
      formData.pickupArea !== null &&
      formData.pickupArea !== undefined &&
      String(formData.pickupArea) === String(id),
    [formData.pickupArea],
  );

  const updatePassengers = (type, increase = true) => {
    const current = formData.passengers?.[type] ?? 0;
    const nextValue = increase ? current + 1 : Math.max(0, current - 1);
    updateField('passengers', {
      ...formData.passengers,
      [type]: nextValue,
    });
  };

  const getPassengersValue = () => {
    const adults = Number(formData.passengers?.adults || 0);
    const children = Number(formData.passengers?.children || 0);
    const infants = Number(formData.passengers?.infants || 0);
    if (children === 0 && infants === 0) {
      return adults === 1 ? '1 guest' : `${Math.max(adults, 0)} guests`;
    }
    const parts = [];
    if (adults) parts.push(`${adults} ${adults === 1 ? 'adult' : 'adults'}`);
    if (children) parts.push(`${children} ${children === 1 ? 'child' : 'children'}`);
    if (infants) parts.push(`${infants} ${infants === 1 ? 'infant' : 'infants'}`);
    return parts.join(', ') || 'Guests';
  };

  const lastQueryRef = useRef('');

  useEffect(() => {
    const params = buildSearchParams(formData);
    const query = params.toString();

    if (query !== lastQueryRef.current) {
      lastQueryRef.current = query;
      startTransition(() => {
        setSearchParams(params, { replace: true });
      });
    }
  }, [formData, buildSearchParams, setSearchParams]);

  const isAboveResults = variant === 'aboveResults';
  const inactiveSurface =
    isAboveResults && bandSurface
      ? getBookSectionSurfaceClasses(bandSurface.backgroundColor, bandSurface.backgroundEffect)
      : null;
  const bandClassName = isAboveResults
    ? [
        'excursions-search-band excursions-search-band--above-results pt-0 pb-0',
        inactiveSurface ? inactiveSurface.sectionClassNames : 'bg-white',
      ].join(' ')
    : `excursions-search-band bg-mode ${searchOnly ? 'pt-4 pt-lg-5' : 'pt-0'}`;
  const rowClassName = isAboveResults ? 'mt-0' : searchOnly ? 'mt-0' : 'mt-n7';
  const colProps = isAboveResults
    ? { xs: 12, className: 'w-100' }
    : { xs: 11, className: 'mx-auto' };

  const dateRangeValue = [
    formData.dateFrom instanceof Date && !isNaN(formData.dateFrom.getTime()) ? formData.dateFrom : null,
    formData.dateTo instanceof Date && !isNaN(formData.dateTo.getTime()) ? formData.dateTo : null,
  ].filter(Boolean);

  if (!showSearchForm) {
    return null;
  }

  return (
    <section className={bandClassName}>
      {inactiveSurface?.hasDotsEffect ? (
        <>
          <span className="section-bg-dots section-bg-dots--left d-none d-xl-block" aria-hidden />
          <span className="section-bg-dots section-bg-dots--right d-none d-lg-block" aria-hidden />
        </>
      ) : null}
      <Container className={inactiveSurface?.hasDotsEffect ? 'position-relative' : undefined}>
        <Row className={rowClassName}>
          <Col {...colProps}>
            <div className="hero-search-section tour-hero-form excursions-search-band__form-shell w-100">
              <form className="excursions-search-toolbar" autoComplete="off" onSubmit={(event) => event.preventDefault()}>
                <div className="excursions-search-toolbar__field excursions-search-toolbar__field--dates">
                  <BsCalendar className="excursions-search-toolbar__icon" size={16} aria-hidden />
                  <Flatpicker
                    className="excursions-search-toolbar__input"
                    options={{
                      mode: 'range',
                      dateFormat: 'j M',
                      closeOnSelect: false,
                      minDate: 'today',
                    }}
                    placeholder="Dates"
                    value={dateRangeValue}
                    getValue={(dates) => {
                      const range = Array.isArray(dates) ? dates.filter((item) => item instanceof Date && !isNaN(item.getTime())) : [];
                      const dateFrom = range[0] || null;
                      const dateTo = range[1] || range[0] || null;
                      setFormData((prev) => {
                        const sameFrom = (prev.dateFrom?.getTime?.() || 0) === (dateFrom?.getTime?.() || 0);
                        const sameTo = (prev.dateTo?.getTime?.() || 0) === (dateTo?.getTime?.() || 0);
                        if (sameFrom && sameTo) return prev;
                        return { ...prev, dateFrom, dateTo };
                      });
                    }}
                  />
                </div>

                <div className="excursions-search-toolbar__field excursions-search-toolbar__field--guests">
                  <Dropdown className="guest-selector w-100">
                    <DropdownToggle
                      as="button"
                      type="button"
                      className="excursions-search-toolbar__control"
                    >
                      <BsPeople className="excursions-search-toolbar__icon" size={16} aria-hidden />
                      <span className="excursions-search-toolbar__label">{getPassengersValue()}</span>
                    </DropdownToggle>
                    <DropdownMenu className="guest-selector-dropdown">
                      <li className="d-flex justify-content-between align-items-center px-3 py-2">
                        <div>
                          <h6 className="mb-0">Adults</h6>
                          <small className="text-muted">13+</small>
                        </div>
                        <div className="hstack gap-1 align-items-center">
                          <button type="button" className="btn btn-link p-0 mb-0" onClick={() => updatePassengers('adults', false)}>
                            <BsDashCircle size={16} />
                          </button>
                          <span className="guest-selector-count mb-0">{formData.passengers?.adults ?? 0}</span>
                          <button type="button" className="btn btn-link p-0 mb-0" onClick={() => updatePassengers('adults')}>
                            <BsPlusCircle size={16} />
                          </button>
                        </div>
                      </li>
                      <DropdownDivider />
                      <li className="d-flex justify-content-between align-items-center px-3 py-2">
                        <div>
                          <h6 className="mb-0">Children</h6>
                          <small className="text-muted">2-12</small>
                        </div>
                        <div className="hstack gap-1 align-items-center">
                          <button type="button" className="btn btn-link p-0 mb-0" onClick={() => updatePassengers('children', false)}>
                            <BsDashCircle size={16} />
                          </button>
                          <span className="guest-selector-count mb-0">{formData.passengers?.children ?? 0}</span>
                          <button type="button" className="btn btn-link p-0 mb-0" onClick={() => updatePassengers('children')}>
                            <BsPlusCircle size={16} />
                          </button>
                        </div>
                      </li>
                      <DropdownDivider />
                      <li className="d-flex justify-content-between align-items-center px-3 py-2">
                        <div>
                          <h6 className="mb-0">Infants</h6>
                          <small className="text-muted">Under 2</small>
                        </div>
                        <div className="hstack gap-1 align-items-center">
                          <button type="button" className="btn btn-link p-0 mb-0" onClick={() => updatePassengers('infants', false)}>
                            <BsDashCircle size={16} />
                          </button>
                          <span className="guest-selector-count mb-0">{formData.passengers?.infants ?? 0}</span>
                          <button type="button" className="btn btn-link p-0 mb-0" onClick={() => updatePassengers('infants')}>
                            <BsPlusCircle size={16} />
                          </button>
                        </div>
                      </li>
                    </DropdownMenu>
                  </Dropdown>
                </div>

                <div className="excursions-search-toolbar__field excursions-search-toolbar__field--language">
                  <LanguageSelect
                    value={formData.language || null}
                    onChange={(value) => {
                      updateField('language', value === '-1' || !value ? null : value);
                    }}
                    placeholder="Language"
                    allOptionLabel="Any language"
                    showIcon={false}
                    showLabel={false}
                    className="excursions-search-toolbar__language"
                  />
                </div>

                <div className="excursions-search-toolbar__field excursions-search-toolbar__field--pickup">
                  <Dropdown className="pickup-selector w-100">
                    <DropdownToggle
                      as="button"
                      type="button"
                      className="excursions-search-toolbar__control"
                      disabled={loadingRegions}
                    >
                      <BsGeoAlt className="excursions-search-toolbar__icon" size={16} aria-hidden />
                      <span className="excursions-search-toolbar__label">{getPickupDisplayValue()}</span>
                    </DropdownToggle>
                    <DropdownMenu className="pickup-selector-dropdown">
                      {loadingRegions ? (
                        <li className="px-3 py-2 text-muted text-center">
                          <small>Loading regions...</small>
                        </li>
                      ) : regions.length === 0 ? (
                        <li className="px-3 py-2 text-muted text-center">
                          <small>No regions available</small>
                        </li>
                      ) : (
                        <>
                          <Dropdown.Item
                            as="button"
                            type="button"
                            className={`reset-option border-bottom px-3 py-2 ${formData.pickupArea === null ? 'active text-primary' : 'text-muted'}`}
                            onClick={(event) => {
                              event.preventDefault();
                              updateField('pickupArea', null);
                            }}
                          >
                            Any pickup
                          </Dropdown.Item>
                          {regions.map((region) => (
                            <Dropdown.Item
                              key={region.id}
                              as="button"
                              type="button"
                              className={`px-3 ${isPickupSelected(region.id) ? 'active' : ''}`}
                              onClick={(event) => {
                                event.preventDefault();
                                updateField('pickupArea', String(region.id));
                              }}
                            >
                              {region.name}
                            </Dropdown.Item>
                          ))}
                        </>
                      )}
                    </DropdownMenu>
                  </Dropdown>
                </div>
              </form>
            </div>
          </Col>
        </Row>

      </Container>
    </section>
  );
};

export default ExcursionsSearchPanel;
